import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import FeatureMedia from '../../pages/Home/HomeTools/FeatureMedia';
import LandingSection from './LandingSection';
import SectionEyebrow from './SectionEyebrow';
import SectionHeading from './SectionHeading';
import { REDESIGN_COLORS, REDESIGN_FONTS } from '../../constants';

const ONE_COL = '@media (max-width: 860px)';

const useStyles = makeStyles(() => ({
    phase: {
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
        margin: '0 0 20px',
    },
    phaseNumber: {
        color: REDESIGN_COLORS.accent,
        fontSize: '0.82rem',
        fontWeight: REDESIGN_FONTS.weightHeading,
    },
    phaseLabel: {
        color: REDESIGN_COLORS.muted,
        fontSize: '0.74rem',
        fontWeight: REDESIGN_FONTS.weightHeading,
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
    },
    phaseRule: {
        flex: 1,
        height: '1px',
        background: REDESIGN_COLORS.ruleSand,
    },
    card: {
        boxSizing: 'border-box',
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 0.85fr) minmax(0, 1.15fr)',
        gap: 'clamp(26px, 3.6vw, 46px)',
        alignItems: 'center',
        marginBottom: '52px',
        padding: 'clamp(24px, 2.6vw, 34px)',
        border: `1px solid ${REDESIGN_COLORS.sand}`,
        borderRadius: '24px',
        background: REDESIGN_COLORS.white,
        // Cards can be deep-link targets; keep them clear of the sticky nav.
        scrollMarginTop: '96px',
        [ONE_COL]: {
            gridTemplateColumns: '1fr',
            gap: '26px',
        },
    },
    // On one column the copy should introduce the visual, not follow it.
    mediaFirst: {
        gridTemplateColumns: 'minmax(0, 1.15fr) minmax(0, 0.85fr)',
        [ONE_COL]: {
            gridTemplateColumns: '1fr',
            '& > :first-child': {
                order: 2,
            },
        },
    },
    cardLast: {
        marginBottom: '8px',
    },
    copy: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
    },
    eyebrow: {
        marginBottom: '13px',
        padding: '6px 11px',
        border: `1px solid ${REDESIGN_COLORS.primaryBorderSoft}`,
        background: REDESIGN_COLORS.mist,
        letterSpacing: '0.09em',
    },
    title: {
        margin: '0 0 11px',
        maxWidth: '26ch',
        fontSize: 'clamp(1.45rem, 2.5vw, 2.1rem)',
        lineHeight: 1.1,
        letterSpacing: '-0.03em',
        fontWeight: REDESIGN_FONTS.weightHeading,
        color: REDESIGN_COLORS.dark,
    },
    text: {
        margin: 0,
        color: REDESIGN_COLORS.body,
        fontSize: '1rem',
        lineHeight: 1.6,
    },
    bullets: {
        display: 'grid',
        gap: '10px',
        margin: '16px 0 0',
        padding: 0,
        listStyle: 'none',
        color: REDESIGN_COLORS.body,
        fontSize: '0.9rem',
        fontWeight: REDESIGN_FONTS.weightStrong,
    },
    bullet: {
        display: 'flex',
        gap: '9px',
    },
    tick: {
        color: REDESIGN_COLORS.primary,
        fontWeight: REDESIGN_FONTS.weightHeading,
    },
    more: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        marginTop: '20px',
        color: REDESIGN_COLORS.link,
        fontSize: '0.92rem',
        fontWeight: REDESIGN_FONTS.weightHeading,
        '&:hover': {
            color: REDESIGN_COLORS.primary,
        },
    },
}));

const cx = (...names) => names.filter(Boolean).join(' ');

/**
 * A run of numbered feature cards, each with its visual on a tinted mat and
 * the copy beside it, alternating sides. `features` is a list of
 * `{ key, src, tint, mediaFirst, more, bullets = 3, fit = 'contain' }`; copy
 * comes from `${keyPrefix}.<key>.phase|label|title|text|bullet<n>|alt` and
 * the heading from `${keyPrefix}.eyebrow|title|lead`, with `${keyPrefix}.more`
 * for the link. With `anchorPrefix`, every card gets the id
 * `${anchorPrefix}${key}` so it can be linked to directly.
 */
const FeatureCards = ({ id, keyPrefix, features, anchorPrefix }) => {
    const classes = useStyles();
    const intl = useIntl();

    const t = (key) => intl.formatMessage({ id: `${keyPrefix}.${key}` });

    return (
        <LandingSection id={id} paddingTop={64} paddingBottom={64}>
            <SectionHeading
                eyebrow={<FormattedMessage id={`${keyPrefix}.eyebrow`} />}
                title={<FormattedMessage id={`${keyPrefix}.title`} />}
                lead={<FormattedMessage id={`${keyPrefix}.lead`} />}
                marginBottom={44}
            />

            {features.map(({ key, src, tint, mediaFirst, more, bullets = 3, fit = 'contain' }, index) => {
                const media = <FeatureMedia key="media" tint={tint} src={src} alt={t(`${key}.alt`)} fit={fit} />;

                const copy = (
                    <div key="copy" className={classes.copy}>
                        <SectionEyebrow variant="card" className={classes.eyebrow}>
                            <FormattedMessage id={`${keyPrefix}.${key}.label`} />
                        </SectionEyebrow>
                        <h3 className={classes.title}>
                            <FormattedMessage id={`${keyPrefix}.${key}.title`} />
                        </h3>
                        <p className={classes.text}>
                            <FormattedMessage id={`${keyPrefix}.${key}.text`} />
                        </p>
                        <ul className={classes.bullets}>
                            {Array.from({ length: bullets }, (_, i) => (
                                <li key={i} className={classes.bullet}>
                                    <span aria-hidden="true" className={classes.tick}>
                                        ✓
                                    </span>
                                    <FormattedMessage id={`${keyPrefix}.${key}.bullet${i + 1}`} />
                                </li>
                            ))}
                        </ul>
                        {more ? (
                            <Link to={more} className={classes.more}>
                                <FormattedMessage id={`${keyPrefix}.more`} />
                                <span aria-hidden="true">→</span>
                            </Link>
                        ) : null}
                    </div>
                );

                return (
                    <React.Fragment key={key}>
                        <div className={classes.phase}>
                            <span className={classes.phaseNumber}>{String(index + 1).padStart(2, '0')}</span>
                            <span className={classes.phaseLabel}>
                                <FormattedMessage id={`${keyPrefix}.${key}.phase`} />
                            </span>
                            <span aria-hidden="true" className={classes.phaseRule} />
                        </div>
                        <article
                            id={anchorPrefix ? `${anchorPrefix}${key}` : undefined}
                            className={cx(
                                classes.card,
                                mediaFirst && classes.mediaFirst,
                                index === features.length - 1 && classes.cardLast
                            )}
                        >
                            {mediaFirst ? [media, copy] : [copy, media]}
                        </article>
                    </React.Fragment>
                );
            })}
        </LandingSection>
    );
};

export default FeatureCards;
